import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CalendarPlus, ChevronDown } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/src/renderer/components/ui/popover'
import { WaveProgress } from '@/src/renderer/components/ui/WaveProgress'
import { MiniCalendar } from '../WhenDropdown/MiniCalendar'
import { timelineState, type TimelineCaption } from './timelineState'

/** Start und Ende eines Projekts — `null` heißt „nicht gesetzt“. */
export interface ProjectTimelineValue {
  startDate: Date | null
  endDate: Date | null
}

export interface ProjectTimelineProps {
  value: ProjectTimelineValue
  onValueChange: (next: ProjectTimelineValue) => void
  /** Nur für Stories und Tests: das „heute“, an dem gerechnet wird. */
  today?: Date
}

/**
 * **Der Zeitraum eines Projekts als Strecke** (#220): links der Start, rechts das Ende, dazwischen
 * die Welle mit dem verstrichenen Anteil und der Marke „heute“, darunter ein Satz. Die beiden Enden
 * sind Knöpfe mit Kalender.
 *
 * Das Ende liegt nie vor dem Start: Sein Kalender sperrt die Tage davor, und ein Start hinter dem
 * Ende zieht das Ende auf denselben Tag mit.
 */
export function ProjectTimeline({ value, onValueChange, today = new Date() }: ProjectTimelineProps) {
  const { startDate, endDate } = value
  const { t, i18n } = useTranslation()
  const state = timelineState(startDate, endDate, today)
  const format = (date: Date) => date.toLocaleDateString(i18n.language, { day: 'numeric', month: 'short', year: 'numeric' })

  const pickStart = (date: Date | null) => {
    const endMoves = date != null && endDate != null && dayOf(endDate) < dayOf(date)
    onValueChange({ startDate: date, endDate: endMoves ? date : endDate })
  }

  return (
    <section className="project-details__card" aria-label={t('projectDetails.timeline.title')}>
      <h3 className="project-details__card-title">{t('projectDetails.timeline.title')}</h3>
      <div className="project-details__ends">
        <DateEnd
          date={startDate}
          placeholder={t('projectDetails.timeline.start')}
          label={t('projectDetails.timeline.pickStart')}
          format={format}
          onPick={pickStart}
        />
        <DateEnd
          date={endDate}
          minDate={startDate ?? undefined}
          placeholder={t('projectDetails.timeline.end')}
          label={t('projectDetails.timeline.pickEnd')}
          format={format}
          onPick={date => onValueChange({ startDate, endDate: date })}
          align="end"
        />
      </div>
      <WaveProgress
        value={state.share ?? 0}
        className={`project-details__wave${state.share == null ? ' project-details__wave--open' : ''}`}
        // Die Marke ist Schmuck: Was sie sagt, steht im Satz darunter.
        marker={state.marker ? <span className="project-details__today" aria-hidden="true" /> : undefined}
      />
      <p className="project-details__caption">{captionText(state.caption, t, format)}</p>
    </section>
  )
}

function captionText(caption: TimelineCaption, t: (key: string, options?: Record<string, unknown>) => string, format: (d: Date) => string): string {
  switch (caption.kind) {
    case 'none': return t('projectDetails.timeline.none')
    case 'upcoming': return t('projectDetails.timeline.upcoming', { count: caption.days })
    case 'left': return t('projectDetails.timeline.left', { count: caption.days })
    case 'endsToday': return t('projectDetails.timeline.endsToday')
    case 'over': return t('projectDetails.timeline.over', { count: caption.days })
    case 'openEnded': return t('projectDetails.timeline.openEnded', { date: format(caption.since) })
  }
}

function dayOf(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

/** Ein Ende der Strecke: das Datum als Knopf, dahinter ein Kalender mit „Entfernen“. */
function DateEnd({
  date, minDate, placeholder, label, format, onPick, align = 'start',
}: {
  date: Date | null
  minDate?: Date
  placeholder: string
  label: string
  format: (d: Date) => string
  onPick: (date: Date | null) => void
  align?: 'start' | 'end'
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`project-details__end${align === 'end' ? ' project-details__end--right' : ''}${date ? '' : ' project-details__end--empty'}`}
          aria-label={label}
        >
          {date ? format(date) : <><CalendarPlus size={14} aria-hidden="true" />{placeholder}</>}
          {/* Wie an den Chips: sagt, dass hier etwas aufgeht (gewünscht 29.09.2026). */}
          <ChevronDown size={14} className="project-details__chevron" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent align={align} className="project-details__calendar">
        <MiniCalendar
          selectedDate={date}
          activeSelection={date != null}
          minDate={minDate}
          onSelect={picked => { onPick(startOfDay(picked)); setOpen(false) }}
        />
        {date && (
          <button type="button" className="project-details__remove" onClick={() => { onPick(null); setOpen(false) }}>
            {t('projectDetails.timeline.remove')}
          </button>
        )}
      </PopoverContent>
    </Popover>
  )
}
